<template>
  <SrpModal v-model:isVisible="_isVisible" size="medium">
    <template #content>
      <!-- Edit Title and Comment form -->
      <div class="edit-title-and-comment-form">
        <div class="standard-page-header standard-page-header--small-bottom-padding edit-title-and-comment-form__header">
          <h1 class="global-h1 standard-page-header__title">
            {{ props.photoGalleryId ? "Edit Group Title and Comment" : "Create a New Group" }}
          </h1>
        </div>

        <!-- Is a Location toggle -->
        <div class="title-and-form-element edit-title-and-comment-form__title-and-form-element">
          <span class="ui toggle checkbox">
            <input type="checkbox" :disabled="isSaving" v-model="isLocationToggled" />
            <label><b>Is this a Location?</b></label>
          </span>
        </div>
        <!-- / Is a Location toggle -->

        <!-- Title and form element -->
        <div class="title-and-form-element edit-title-and-comment-form__title-and-form-element">
          <h4 v-if="!isLocationToggled" class="global-h4 title-and-form-element__title">Title</h4>
          <template v-if="isLocationToggled">
            <GMapAutocomplete
              ref="locationInputRef"
              :value="_title"
              placeholder="Search for a location"
              @place_changed="googlePlaceChosen"
              @input="e => placeTextChanged(e.target.value)"
              :options="{
                fields: ['name', 'place_id', 'address_components', 'formatted_address', 'geometry'],
              }"
              style="max-width: 390px"
              :class="{ 'global-text-input': true, 'global-text-input--error': locationValidationError }"
              :disabled="isSaving"
            />
            <div v-if="locationValidationError" style="margin-top: 6px; color: #d32f2f; font-size: 14px; font-weight: 500">
              {{ locationValidationError }}
            </div>
            <div v-else-if="_address" style="margin-top: 5px; margin-left: 6px; color: rgba(0, 0, 0, 0.6); font-size: 14px">
              {{ _address }}
            </div>
          </template>
          <template v-else>
            <input class="global-text-input title-and-form-element__form-element" type="text" style="max-width: 390px" v-model="_title" :disabled="isSaving" placeholder="Summer Festival Highlights" />
          </template>
        </div>
        <!-- / Title and form element -->

        <!-- Title and form element -->
        <div class="title-and-form-element edit-title-and-comment-form__title-and-form-element">
          <h4 class="global-h4 title-and-form-element__title">Description / Notes <span style="font-weight: 500; color: rgba(0, 0, 0, 0.5)">(optional)</span></h4>
          <textarea
            class="global-textarea title-and-form-element__form-element"
            style="height: 110px; min-height: 110px"
            v-model="_comment"
            :disabled="isSaving"
            placeholder="Location or event name, theme, etc."
          />
        </div>
        <!-- / Title and form element -->

        <!-- Photo Credit -->
        <div class="title-and-form-element edit-title-and-comment-form__title-and-form-element">
          <h4 class="global-h4 title-and-form-element__title">Photo Credit <span style="font-weight: 500; color: rgba(0, 0, 0, 0.5)">(optional)</span></h4>
          <input class="global-text-input title-and-form-element__form-element" type="text" style="max-width: 390px" v-model="_photoCredit" :disabled="isSaving" />
        </div>
        <!-- / Photo Credit -->
      </div>
      <!-- / Edit Title and Comment form -->
    </template>

    <template #footer="{ closeModal }">
      <SrpButton color="gray" fill="outlined" style="margin-right: 12px" @click="closeModal" :isDisabled="isSaving">Cancel</SrpButton>

      <SrpButton v-if="props.photoGalleryId" @click="saveAndCloseModal(closeModal)" :isDisabled="isSaving || !_title">
        <template #icon>
          <LoadingIcon v-if="isSaving" :size="22" />
          <IconEmbedded v-else name="save_2" :size="22" color="rgba(255, 255, 255, 0.4)" />
        </template>
        Save
      </SrpButton>

      <SrpButton v-else @click="saveAndCloseModal(closeModal)" :isDisabled="isSaving || !_title">
        <template v-if="isSaving" #icon><LoadingIcon :size="22" /></template>
        Create
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from "vue";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";
import AddressHelper from "@helpers/AddressHelper";
import { useLogger } from "@helpers/Logger";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Types
import { PhotoGallerySource, PhotoGalleryCustomerData } from "@contracts/photosPageData";

const logger = useLogger();
const remoteLogger = getGlobalRemoteLogger();

const props = withDefaults(
  defineProps<{
    title?: string | null;
    comment?: string | null;
    photoCredit?: string | null;
    customerId: string | null;
    photoGalleryId?: string | null;
    currentOperationSource?: PhotoGallerySource;
    locationExternalId?: string | null;
    locationName?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    shouldOpenWithLocationToggle?: boolean;
  }>(),
  {
    title: null,
    comment: null,
    photoCredit: null,
    customerId: null,
    photoGalleryId: null,
    currentOperationSource: PhotoGallerySource.ContentLibraryManual,
    locationExternalId: null,
    locationName: null,
    address: null,
    city: null,
    state: null,
    country: null,
    shouldOpenWithLocationToggle: false,
  }
);

const emit = defineEmits<{
  (e: "save", upsertedPhotoGallery: PhotoGalleryCustomerData): void;
}>();

// Private isVisible ==========================================================
const _isVisible = ref<boolean>(false);

// Title and comment ==========================================================
const _title = ref<string>("");
const _comment = ref<string>("");
const _photoCredit = ref<string>("");

// Location data ==============================================================
const isLocationToggled = ref<boolean>(false);
const _locationExternalId = ref<string | null>(null);
const _locationName = ref<string | null>(null);
const _address = ref<string | null>(null);
const _city = ref<string | null>(null);
const _state = ref<string | null>(null);
const _country = ref<string | null>(null);
const locationInputRef = ref<any>(null);
const locationValidationError = ref<string>("");

onMounted(() => {
  _title.value = props.title;
  _comment.value = props.comment || "";
  _photoCredit.value = props.photoCredit || "";

  // Initialize location data
  _locationExternalId.value = props.locationExternalId;
  _locationName.value = props.locationName;
  _address.value = props.address;
  _city.value = props.city;
  _state.value = props.state;
  _country.value = props.country;

  // Set toggle based on whether location data exists or if explicitly requested
  isLocationToggled.value = props.shouldOpenWithLocationToggle || !!props.locationExternalId;

  // Focus location input if requested
  if (props.shouldOpenWithLocationToggle) {
    nextTick(() => {
      locationInputRef.value?.$el?.focus();
    });
  }
});

// Watch for location toggle changes to clear data when turned off
watch(isLocationToggled, newValue => {
  if (!newValue) {
    // Clear location data when toggle is turned off
    _locationExternalId.value = null;
    _locationName.value = null;
    _address.value = null;
    _city.value = null;
    _state.value = null;
    _country.value = null;
  }
});

// Google Place handlers ======================================================
function googlePlaceChosen(place: globalThis.google.maps.places.PlaceResult) {
  try {
    if (place?.geometry?.location) {
      _locationExternalId.value = place.place_id;
      _title.value = place.name;
      _locationName.value = place.name;
      _address.value = AddressHelper.removeCountryAndZipCode(place.formatted_address);
      // Note: Similar logic in GooglePlaceConverter
      _country.value = place.address_components.find(c => c.types.includes("country"))?.short_name;
      _state.value = place.address_components.find(c => c.types.includes("administrative_area_level_1"))?.short_name;
      _city.value = place.address_components.find(c => c.types.includes("locality"))?.short_name;
      // If city is null fallback to AdminAreaL3 (typically a Township), then L2 (typically a county)
      if (!_city.value) {
        _city.value = place.address_components.find(c => c.types.includes("administrative_area_level_3"))?.short_name;
      }
      if (!_city.value) {
        _city.value = place.address_components.find(c => c.types.includes("administrative_area_level_2"))?.short_name;
      }

      // Clear validation error when a valid location is selected
      locationValidationError.value = "";

      logger.info(`googlePlaceChosen: ${place?.name}, City=${_city.value}, State=${_state.value}, Country=${_country.value} Id=${place?.place_id}`);
    }
  } catch (ex) {
    remoteLogger.error(`Place Autocomplete failure: Input=${_title.value} Name=${place?.name} PlaceId=${place?.place_id}  ${ex.message}`);
  }
}

function placeTextChanged(newTitle: string) {
  logger.info("PlaceTextChanged: " + newTitle);
  const cleanedTitle = AddressHelper.removeCountryAndZipCode(newTitle);
  _title.value = cleanedTitle;
  _locationName.value = cleanedTitle;

  // Clear validation error when user types
  locationValidationError.value = "";

  // If they cleared their location, null out the location data
  if (!newTitle || newTitle.trim().length === 0) {
    _locationExternalId.value = null;
    _locationName.value = null;
    _address.value = null;
    _city.value = null;
    _state.value = null;
    _country.value = null;
    logger.info(`Clearing location fields:`);
  }
}

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function saveAndCloseModal(closeModalCallback: () => void) {
  const success = await save();
  if (!success) {
    return;
  }
  closeModalCallback?.();
}

async function save() {
  // Validate location if toggle is on
  if (isLocationToggled.value && !_locationExternalId.value) {
    locationValidationError.value = "Please select a location from the autocomplete suggestions.";
    return false;
  }

  isSaving.value = true;

  let upsertedPhotoGallery: PhotoGalleryCustomerData = null;

  if (props.photoGalleryId) {
    upsertedPhotoGallery = await PhotoGalleryV2Repo.updateMyUploadsGroupCommentAndTitle(
      props.customerId,
      props.photoGalleryId,
      _title.value,
      _comment.value,
      props.currentOperationSource,
      _photoCredit.value,
      _locationExternalId.value,
      _locationName.value,
      _address.value,
      _city.value,
      _state.value,
      _country.value
    );
  } else {
    upsertedPhotoGallery = await PhotoGalleryV2Repo.createMyUploadsGroup(
      props.customerId,
      _title.value,
      _comment.value,
      props.currentOperationSource,
      _photoCredit.value,
      _locationExternalId.value,
      _locationName.value,
      _address.value,
      _city.value,
      _state.value,
      _country.value
    );
  }

  isSaving.value = false;

  emit("save", upsertedPhotoGallery);

  return true;
}
</script>

<style scoped lang="scss">
// Title and form element =====================================================
.title-and-form-element {
  &__title {
    margin-bottom: 7px;
  }

  &__form-element {
  }
}

// Edit Title and Comment form ================================================
.edit-title-and-comment-form {
  &__header {
    margin-bottom: 18px;
  }

  &__form-itself {
  }

  &__title-and-form-element {
    margin-bottom: 28px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
